import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Dropdown } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-date-picker';
import axios from 'axios';
import Form from 'react-bootstrap/Form';
import moment from 'moment';
import useModal from '../../utils/hooks/useModal';
import { ADS_ADMIN_PREFIX } from '../../utils/constants';
import ConfirmationModal from '../search/widgets/ConfirmationModal';
import AdditionalInfoTextArea from '../search/seller-dashboard/seller-ads/AdditionalInfoTextArea';
import routes from '../../utils/routes';

interface Props {
  listing: {
    listingType: string,
    listingID: number|string,
    litterTitle?: string,
  },
  similarLitters: Array<{
    label: string,
    value: number|string,
  }>,
  canDelete: boolean,
  canSetExpiration: boolean,
  canUpdateLitter: boolean,
}

const ListingPageAdminActions: React.FC<Props> = ({
  listing,
  similarLitters,
  canDelete,
  canSetExpiration,
  canUpdateLitter,
}) => {
  const {
    listingID,
    listingType,
    litterTitle,
  } = listing;

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
  } = useModal();

  const performAction = useCallback((action: string) => {
    const pageReload = () => {
      performAction('');
      setModalFormState({});
      window.location.reload();
    };
    switch (action) {
      case 'delete':
        setModalData({
          message: 'Would you like to delete this ad?',
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${ADS_ADMIN_PREFIX}/${listingType}/${listingID}`)
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            performAction('');
            setModalFormState({});
          },
          onSuccess: () => {
            // TODO: Navigate to admin?
            window.location.href = routes.ADMIN_INDEX;
          },
        });
        break;
      case 'soft_delete':
        setModalData({
          message: 'Would you like to delete this ad?',
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.patch(
                `${ADS_ADMIN_PREFIX}/${listingType}/${listingID}`,
                { status: 'unpublished' },
              )
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            performAction('');
            setModalFormState({});
          },
          onSuccess: () => {
            // TODO: Navigate to admin?
            window.location.href = routes.ADMIN_INDEX;
          },
        });
        break;
      case 'set_sold':
        setModalData({
          message: 'Would you like to update the \'Sold Status\' of this ad?',
          ModalForm: ({ formState }) => {
            useEffect(() => {
              if (formState.availability !== 'sold') {
                setModalFormState((prevState) => ({ ...prevState, sold_through: '', sold_through_additional_info: '' }));
              }
            }, [formState.availability]);
            return (
              <div>
                <Select
                  className="mt-1"
                  placeholder="Choose a status..."
                  onChange={(e) => {
                    setModalFormState((prevState) => ({ ...prevState, availability: e?.value }));
                  }}
                  options={[
                    {
                      label: 'Available',
                      value: 'available',
                    },
                    {
                      label: 'Sold',
                      value: 'sold',
                    },
                    {
                      label: 'Pending',
                      value: 'pending',
                    },
                  ]}
                />
                {formState?.availability === 'sold' && (
                  <div>
                    <div className="my-1">How was puppy sold?</div>
                    <Select
                      placeholder="Choose an option..."
                      onChange={(e) => {
                        // eslint-disable-next-line max-len
                        setModalFormState((prevState) => ({ ...prevState, sold_through: e?.value }));
                      }}
                      options={[
                        {
                          label: 'Lancaster Puppies',
                          value: 'lancaster_puppies',
                        },
                        {
                          label: 'Other Advertising Site',
                          value: 'other_site',
                        },
                        {
                          label: 'Word of Mouth',
                          value: 'word_of_mouth',
                        },
                        {
                          label: 'Other',
                          value: 'other',
                        },
                      ]}
                    />
                    <AdditionalInfoTextArea
                      formState={formState}
                      onChange={(e) => {
                        setModalFormState((prevState) => ({
                          ...prevState,
                          sold_through_additional_info: e.target.value,
                        }));
                      }}
                    />
                  </div>
                )}
              </div>
            );
          },
          onConfirmAsync: (fields) => {
            return new Promise((res, rej) => {
              axios.patch(
                `${ADS_ADMIN_PREFIX}/${listingType}/${listingID}`,
                fields,
              )
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            performAction('');
            setModalFormState({});
          },
          onSuccess: () => {
            pageReload();
          },
        });
        break;
      case 'set_published':
        setModalData({
          message: 'Would you like to update the \'Published Status\' of this ad?',
          ModalForm: () => {
            return (
              <div>
                <Select
                  placeholder="Choose a status..."
                  onChange={(e) => {
                    setModalFormState({ status: e?.value });
                  }}
                  options={[
                    {
                      label: 'Published',
                      value: 'published',
                    },
                    {
                      label: 'Unpublished',
                      value: 'unpublished',
                    },
                    {
                      label: 'Draft',
                      value: 'draft',
                    },

                  ]}
                />
              </div>
            );
          },
          onConfirmAsync: (fields) => {
            return new Promise((res, rej) => {
              axios.patch(
                `${ADS_ADMIN_PREFIX}/${listingType}/${listingID}`,
                fields,
              )
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            performAction('');
            setModalFormState({});
          },
          onSuccess: () => {
            pageReload();
          },
        });
        break;
      case 'set_expiration':
        setModalData({
          message: 'Would you like to update the \'Expiration Date\' of this ad?',
          ModalForm: () => {
            const [date, setDate] = useState(new Date());
            const [cannotExpire, setCannotExpire] = useState(false);
            useEffect(() => {
              if (date) {
                setModalFormState({
                  expires_at: moment.utc(date),
                  can_expire: 1,
                });
              } else if (cannotExpire) {
                setModalFormState({
                  can_expire: 0,
                });
              } else {
                setModalFormState({});
              }
            }, [date, cannotExpire]);
            return (
              <div>
                <Form>
                  <DatePicker
                    onChange={(val) => setDate(val)}
                    value={date}
                    disabled={cannotExpire}
                  />
                  {cannotExpire && (
                    <Form.Text className="text-warning">Cannot select a date if "Cannot Expire" is checked!</Form.Text>
                  )}
                  <Form.Check
                    type="checkbox"
                    id="mark-no-expire"
                    label='Mark these ads "Cannot Expire"'
                    disabled={!!date}
                    checked={cannotExpire}
                    onChange={() => setCannotExpire(!cannotExpire)}
                  />
                  {!!date && (
                    <Form.Text className="text-warning">Cannot select this option if a date is selected for expiration!</Form.Text>
                  )}
                </Form>
              </div>
            );
          },
          onConfirmAsync: (fields) => {
            return new Promise((res, rej) => {
              axios.patch(
                `${ADS_ADMIN_PREFIX}/${listingType}/${listingID}`,
                fields,
              )
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            performAction('');
            setModalFormState({});
          },
          onSuccess: () => {
            pageReload();
          },
        });
        break;
      case 'update_litter':
        setModalData({
          message: 'Would you like to move this ad to a different litter?',
          ModalForm: () => {
            return (
              <div>
                <Select
                  placeholder="Choose a litter..."
                  isSearchable
                  onChange={(e) => {
                    setModalFormState({ litter_id: e?.value });
                  }}
                  options={similarLitters}
                />
                <div className="alert alert-info mt-2" role="alert">
                  <p className="m-0">
                    <b>Current Litter: </b>
                    {litterTitle}
                  </p>
                </div>
              </div>
            );
          },
          onConfirmAsync: (fields) => {
            return new Promise((res, rej) => {
              axios.patch(
                `${ADS_ADMIN_PREFIX}/${listingType}/${listingID}`,
                fields,
              )
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            performAction('');
            setModalFormState({});
          },
          onSuccess: () => {
            pageReload();
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
          isSimple: true,
        });
    }
  }, [modalFormState, listingType, listingID, litterTitle, similarLitters]);

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          className="mx-2 btn btn-primary"
          id="action-dropdown"
        >
          Actions
        </Dropdown.Toggle>
        <Dropdown.Menu className="dropdown-menu-right">
          <Dropdown.Item
            disabled={!listingID || !listingType}
            className="dropdown-item"
            href="#"
            onClick={() => performAction('set_sold')}
          >
            Set Sold Status
          </Dropdown.Item>
          <Dropdown.Item
            disabled={!listingID || !listingType}
            className="dropdown-item"
            href="#"
            onClick={() => {
              if (canDelete) {
                performAction('set_published');
              } else {
                performAction('soft_delete');
              }
            }}
          >
            {canDelete ? 'Set Published Status' : 'Delete'}
          </Dropdown.Item>
          {canSetExpiration && (
            <Dropdown.Item
              disabled={!listingID || !listingType}
              className="dropdown-item"
              href="#"
              onClick={() => performAction('set_expiration')}
            >
              Set Expiration
            </Dropdown.Item>
          )}
          {canUpdateLitter && (
            <Dropdown.Item
              disabled={!listingID || !listingType}
              className="dropdown-item"
              href="#"
              onClick={() => performAction('update_litter')}
            >
              Move to Another Litter
            </Dropdown.Item>
          )}
          {canDelete && (
            <Dropdown.Item
              disabled={!listingID || !listingType}
              className="dropdown-item"
              href="#"
              onClick={() => performAction('delete')}
            >
              Delete
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        {...modalData}
      />
    </>
  );
};

if (
  document.getElementById('react-admin-actions-box')
  || document.getElementById('react-seller-actions-box')
) {
  const element = document.getElementById('react-admin-actions-box')
    || document.getElementById('react-seller-actions-box');

  const data = element?.dataset?.listing
    ? JSON.parse(element.dataset.listing as string)
    : null;

  const similarLitters = element?.dataset?.similarlitters
    ? JSON.parse(element.dataset.similarlitters as string)
    : [];

  const canDelete = element?.dataset?.candelete
    ? element?.dataset.candelete === '1'
    : false;

  const canSetExpiration = element?.dataset?.cansetexpiration
    ? element?.dataset.cansetexpiration === '1'
    : false;

  const canUpdateLitter = element?.dataset?.canupdatelitter
    ? element?.dataset.cansetexpiration === '1'
    : false;

  ReactDOM.render(
    <ListingPageAdminActions
      listing={data}
      similarLitters={similarLitters}
      canDelete={canDelete}
      canSetExpiration={canSetExpiration}
      canUpdateLitter={canUpdateLitter}
    />,
    element,
  );
}
